import { getDefaultDayNightPackage } from 'utils/CommonFun';
import { REGION_CODE_TO_NAME } from 'constants/regionNames';

/** Default office coords used when destination is not geocoded in admin */
const OFFICE_LAT = 12.925506;
const OFFICE_LNG = 77.585944;

/** Keyword → [latitude, longitude] (destination names from itineraries / package titles) */
const DESTINATION_COORDINATES = [
  ['REYKJAVIK', [64.1466, -21.9426]],
  ['ICELAND', [64.9631, -19.0208]],
  ['LANGKAWI', [6.3500, 99.8000]],
  ['KUALA LUMPUR', [3.1390, 101.6869]],
  ['MALAYSIA', [4.2105, 101.9758]],
  ['SHIRDI', [19.7604, 74.4770]],
  ['MAHARASHTRA', [19.7515, 75.7139]],
  ['DEHRADUN', [30.3165, 78.0322]],
  ['HARIDWAR', [29.9457, 78.1642]],
  ['GUPTKASHI', [30.5255, 79.0806]],
  ['KEDARNATH', [30.7346, 79.0669]],
  ['BADRINATH', [30.7433, 79.4938]],
  ['DO DHAM', [30.7346, 79.0669]],
  ['HIMALAYA', [30.9849, 78.7753]],
  ['OOTY', [11.4102, 76.6950]],
  ['MUNNAR', [10.0889, 76.2673]],
  ['COCHIN', [9.9312, 76.2673]],
  ['KOCHI', [9.9312, 76.2673]],
  ['KERALA', [10.8505, 76.2711]],
  ['GOA', [15.2993, 74.1240]],
  ['GANGTOK', [27.3389, 88.6065]],
  ['DARJEELING', [27.0360, 88.2627]],
  ['SIKKIM', [27.5330, 88.5122]],
  ['GUWAHATI', [26.1445, 91.7362]],
  ['KAZIRANGA', [26.5775, 93.1711]],
  ['MEGHALAYA', [25.4670, 91.3662]],
  ['ASSAM', [26.2006, 92.9376]],
  ['VADODARA', [22.3072, 73.1812]],
  ['AHMADABAD', [23.0225, 72.5714]],
  ['AHMEDABAD', [23.0225, 72.5714]],
  ['GUJARAT', [22.2587, 71.1924]],
  ['RANN', [23.7337, 70.9604]],
  ['KASHMIR', [34.0837, 74.7973]],
  ['SRINAGAR', [34.0837, 74.7973]],
  ['LADAKH', [34.1526, 77.5771]],
  ['LEH', [34.1526, 77.5771]],
  ['MANALI', [32.2396, 77.1887]],
  ['SHIMLA', [31.1048, 77.1734]],
  ['DALHOUSIE', [32.5433, 75.9699]],
  ['DHARAMSHALA', [32.2190, 76.3234]],
  ['ANDAMAN', [11.7401, 92.6586]],
  ['LAKSHADWEEP', [10.5667, 72.6417]],
  ['LONDON', [51.5074, -0.1278]],
  ['UNITED KINGDOM', [55.3781, -3.4360]],
  ['UK', [55.3781, -3.4360]],
  ['ENGLAND', [52.3555, -1.1743]],
  ['SCOTLAND', [56.4907, -4.2026]],
  ['EDINBURGH', [55.9533, -3.1883]],
  ['MANCHESTER', [53.4808, -2.2426]],
  ['PARIS', [48.8566, 2.3522]],
  ['DUBAI', [25.2048, 55.2708]],
  ['SINGAPORE', [1.3521, 103.8198]],
  ['BALI', [-8.3405, 115.0920]],
  ['THAILAND', [15.8700, 100.9925]],
  ['BANGKOK', [13.7563, 100.5018]],
  ['NEPAL', [27.7172, 85.3240]],
  ['BHUTAN', [27.5142, 90.4336]],
  ['MALDIVES', [3.2028, 73.2207]],
  ['SRILANKA', [7.8731, 80.7718]],
  ['SRI LANKA', [7.8731, 80.7718]],
];

const REGION_CENTROIDS = Object.entries(REGION_CODE_TO_NAME).map(([code, name]) => ({
  code,
  name: name.toLowerCase(),
  coords: getCentroidForCode(code),
}));

function getCentroidForCode(code) {
  const known = {
    IN: [20.5937, 78.9629],
    GB: [55.3781, -3.4360],
    US: [37.0902, -95.7129],
    FR: [46.2276, 2.2137],
    DE: [51.1657, 10.4515],
    IT: [41.8719, 12.5674],
    ES: [40.4637, -3.7492],
    AE: [23.4241, 53.8478],
    TH: [15.8700, 100.9925],
    SG: [1.3521, 103.8198],
    MY: [4.2105, 101.9758],
    AU: [-25.2744, 133.7751],
    JP: [36.2048, 138.2529],
    CN: [35.8617, 104.1954],
    NP: [28.3949, 84.1240],
    LK: [7.8731, 80.7718],
    MV: [3.2028, 73.2207],
  };
  return known[code] || [0, 0];
}

const isOfficeCoords = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  if (!la || !ln) return false;
  return Math.abs(la - OFFICE_LAT) < 0.02 && Math.abs(ln - OFFICE_LNG) < 0.02;
};

const collectDestinationText = (pkg) => {
  const parts = [pkg.name, pkg.shortDescription, pkg.address?.address];
  const defaultDn = pkg.dayNight?.find((dn) => dn.isDefault && !dn.isDeleted)
    || pkg.dayNight?.find((dn) => !dn.isDeleted)
    || pkg.dayNight?.[0];
  defaultDn?.destinationPoints?.forEach((pt) => {
    if (pt?.name) parts.push(pt.name);
    if (pt?.details) parts.push(pt.details);
  });
  return parts.filter(Boolean).join(' ').toUpperCase();
};

export const resolvePackageLatLng = (pkg) => {
  const lat = Number(pkg.address?.latitude);
  const lng = Number(pkg.address?.longitude);
  if (lat && lng && !isOfficeCoords(lat, lng)) {
    return [lat, lng];
  }

  const text = collectDestinationText(pkg);
  for (const [keyword, coords] of DESTINATION_COORDINATES) {
    if (text.includes(keyword)) {
      return coords;
    }
  }

  const addrLower = (pkg.address?.address || '').toLowerCase();
  for (const { name, coords } of REGION_CENTROIDS) {
    if (addrLower.includes(name) && coords[0] !== 0) {
      return coords;
    }
  }

  if (lat && lng) return [lat, lng];
  return null;
};

/** Slight offset so stacked markers remain visible */
const spreadDuplicateCoords = (items) => {
  const buckets = new Map();
  return items.map((item) => {
    const key = `${item.latLng[0].toFixed(3)},${item.latLng[1].toFixed(3)}`;
    const index = buckets.get(key) || 0;
    buckets.set(key, index + 1);
    if (index === 0) return item;
    const angle = (index * 137.5 * Math.PI) / 180;
    const offset = 0.35 * index;
    return {
      ...item,
      latLng: [
        item.latLng[0] + offset * Math.cos(angle),
        item.latLng[1] + offset * Math.sin(angle),
      ],
    };
  });
};

export const buildPackageMarkerTooltipHtml = (pkg) => {
  const dp = getDefaultDayNightPackage(pkg);
  const price = dp?.price ?? 0;
  const duration = dp?.duration ?? '';
  const location = pkg.address?.address || '';
  const desc = (pkg.shortDescription || '').substring(0, 80);

  return `
    <div class="p-2 max-w-[260px]">
      <div class="flex gap-3">
        <div class="relative w-[72px] h-[72px] rounded-lg overflow-hidden flex-shrink-0">
          <img 
            src="${pkg.imageUrl || '/placeholder-image.jpg'}" 
            alt="${pkg.name}"
            class="w-full h-full object-cover"
          />
        </div>
        <div class="flex-1 min-w-0">
          <div class="font-bold text-sm mb-1 leading-tight">${pkg.name}</div>
          <div class="text-xs text-gray-300 mb-1 line-clamp-2">${desc}${desc.length >= 80 ? '…' : ''}</div>
          <div class="text-orange-400 font-semibold text-xs">From ₹${Number(price).toLocaleString('en-IN')}</div>
          ${duration ? `<div class="text-gray-400 text-xs mt-0.5">${duration}</div>` : ''}
          ${location ? `<div class="text-gray-500 text-[10px] mt-1 line-clamp-1">${location}</div>` : ''}
        </div>
      </div>
    </div>
  `;
};

export const buildMarkersFromPackages = (packages) => {
  if (!packages?.length) return [];

  const markers = packages
    .map((pkg) => {
      const latLng = resolvePackageLatLng(pkg);
      if (!latLng || !latLng[0] || !latLng[1]) return null;
      return {
        name: buildPackageMarkerTooltipHtml(pkg),
        latLng,
        packageId: pkg._id,
      };
    })
    .filter(Boolean);

  return spreadDuplicateCoords(markers);
};
